import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Space, message, Card, Tag, Switch } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import CreatePromoCodeModal from "./CreatePromoCodeModal";
import axios from "axios";

const PromoCode = () => {
  const [form] = Form.useForm();
  const [promoCodes, setPromoCodes] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentLoadingId, setCurrentLoadingId] = useState(null);

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_ADMIN_API_URL}promocode/all`, {
        headers: {
          "x-product-type": "PIP_GRID",
        },
      })
      .then((res) => {
        const data = res?.data?.data?.map((item) => {
          return {
            count: `${item + 1}`,
            key: item?._id,
            code: item?.code,
            discountType: item?.discountType,
            // discountPercentage: item?.discountPercentage,
            discountAmount: item?.discountAmount,
            promoUseType: item?.promoUseType,
            expiresAt: item?.expiresAt,
            status: item?.status,
            id: item?._id,
            affiliates: item?.affiliates,
            affiliateUserList: item?.affiliates?.map((affiliate) => affiliate?.fullName),
          };
        });
        setPromoCodes(data || []);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
        message.error("Failed to fetch promo codes");
      });
  };

  const columns = [
    {
      title: "Promo Code",
      dataIndex: "code",
      key: "code",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => <span>{record.discountType === "percentage" ? `${record.discountAmount.toFixed(2)}%` : `$${record.discountAmount.toFixed(2)}`}</span>,
    },
    {
      title:"Affiliate User",
      dataIndex: "affiliateUserList",
      key: "affiliateUserList",
      render: (_,record)=><span>
        {record.affiliateUserList?.join(", ") || "-"}
      </span>
    },
    {
      title: "Promo Use Type",
      dataIndex: "promoUseType",
      key: "promoUseType",
      render: (type) => <Tag color={type === "SINGLE" ? "blue" : "green"}>{type === "SINGLE" ? "Single Use" : "Multiple Use"}</Tag>,
    },
    {
      title: "Expires At",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (date) => dayjs(date).format("MMM D, YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => <Tag color={status === "ACTIVE" ? "green" : "red"}>{status.toUpperCase()}</Tag>,
    },
    {
      title: "Active/Deactive ",
      key: "toggle",
      align: "center",
      width: 120,
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Switch
            checked={record.status === "ACTIVE"}
            onChange={() => handleStatusToggle(record, record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE")}
            loading={record.id === currentLoadingId}
            style={{
              backgroundColor: record.status === "ACTIVE" ? "##bffe66" : undefined,
            }}
          />
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            style={{
              color: "#1890ff",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "24px",
            }}
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record.code);
            }}
            style={{
              border: "1px solid #ff4d4f",
              borderRadius: "4px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "24px",
            }}
          />
        </Space>
      ),
    },
  ];

  const handleAddNew = () => {
    setEditingPromo(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    form.setFieldsValue({
      ...promo,
      expiresAt: promo.expiresAt ? dayjs(promo.expiresAt) : null,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Promo Code",
      content: "Are you sure you want to delete this promo code?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          await axios.delete(`${process.env.REACT_APP_ADMIN_API_URL}/promocode/delete/${id}`, {
            headers: {
              "x-product-type": "PIP_GRID",
              "Authorization": `Bearer ${localStorage.getItem("userToken")}`
            },
          });
          fetchList();
          message.success("Promo code deleted successfully");
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message || "Failed to delete promo code";
          message.error(errorMessage);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleStatusToggle = async (record, newStatus) => {
    try {
      setCurrentLoadingId(record.id);
      await axios.put(
        `${process.env.REACT_APP_ADMIN_API_URL}promocode/update`,
        { status: newStatus, code: record.code },
        { headers: { "x-product-type": "PIP_GRID", "Authorization": `Bearer ${localStorage.getItem("userToken")}` } }
      );
      message.success(`Promo code ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
      fetchList();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to update promo code status";
      message.error(errorMessage);
    } finally {
      setCurrentLoadingId(null);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
    
      if (editingPromo) {
        await axios.put(`${process.env.REACT_APP_ADMIN_API_URL}promocode/update`, values, { headers:
           { "x-product-type": "PIP_GRID",
            "Authorization": `Bearer ${localStorage.getItem("userToken")}`
            } });
        message.success("Promo code updated successfully!");
      } else {
        await axios.post(`${process.env.REACT_APP_ADMIN_API_URL}promocode/create`, values, { headers:
           { "x-product-type": "PIP_GRID",
            "Authorization": `Bearer ${localStorage.getItem("userToken")}`
            } });
        message.success("Promo code created successfully!");
      }
      fetchList();
      setEditingPromo(null);
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Something went wrong";
      message.error(`Failed to ${editingPromo ? "update" : "create"} promo code: ${errorMessage}`);
    } finally {
      setLoading(false);
      setIsModalVisible(false);
    }
  };

  return (
    <div className="promo-code-container">
      <Card
        title="Promo Codes"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
            Add Promo Code
          </Button>
        }
      >
        <Table columns={columns} dataSource={promoCodes} rowKey="id" loading={loading} pagination={{ pageSize: 8 }} />
      </Card>
      {isModalVisible && (
        <CreatePromoCodeModal
          editingPromo={editingPromo}
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          loading={loading}
          handleSubmit={handleSubmit}
        />
      )}
    </div>
  );
};
export default PromoCode;
